import { useState, useEffect, useCallback } from 'react';
import { ClipboardList, Loader2, ExternalLink, X, AlertTriangle } from 'lucide-react';
import { apiCall } from './useAutoSave';
import { fmtCena } from './receptUtils';
import Dropdown from './Dropdown';
import TypBadge from './TypBadge';

const KANALY = [
    { id: 'bistro', label: 'Bistro', popis: 'na talíř' },
    { id: 'rozvoz', label: 'Rozvoz', popis: 'k zákazníkovi' },
    { id: 'vydej',  label: 'Výdej',  popis: 'odběr na místě' },
];

/**
 * Balení položky — vybraný balicí předpis, zobrazený jen ke čtení.
 *
 * Předpis sdílí víc položek (jeden „Balení pizza 32 cm" nese devadesát pizz),
 * takže se needituje tady, ale v Balírně. Jinak by se rozešel mezi položkami,
 * které na něj ukazují.
 */
export default function ReceptBaleni({ recept, isDark, onNavigate, onZmena }) {
    const [predpisy, setPredpisy] = useState([]);
    const [detail, setDetail] = useState(null);
    const [nacitam, setNacitam] = useState(false);
    const [uklada, setUklada] = useState(false);
    const [chyba, setChyba] = useState('');

    const secondaryColor = isDark ? 'rgba(255,255,255,0.55)' : 'var(--text-secondary)';
    const mutedColor = isDark ? 'rgba(255,255,255,0.35)' : 'var(--text-muted)';
    const borderColor = isDark ? 'rgba(255,255,255,0.08)' : 'var(--border-default)';
    const BALIRNA = '#10B981';

    useEffect(() => {
        apiCall('/api/balici-predpisy')
            .then(d => setPredpisy(d.filter(p => p.aktivni)))
            .catch(() => {});
    }, []);

    const nactiDetail = useCallback(async (id) => {
        if (!id) { setDetail(null); return; }
        setNacitam(true);
        try { setDetail(await apiCall(`/api/balici-predpisy/${id}`)); }
        catch { setDetail(null); }
        setNacitam(false);
    }, []);

    useEffect(() => { nactiDetail(recept?.balici_predpis_id); }, [recept?.balici_predpis_id, nactiDetail]);

    const priradit = async (predpisId) => {
        setUklada(true); setChyba('');
        try {
            await apiCall(`/api/recepty/recepty/${recept.id}`, 'PATCH', { balici_predpis_id: predpisId || null });
            onZmena?.();
        } catch (e) { setChyba(e?.error || 'Uložení se nepovedlo'); }
        setUklada(false);
    };

    const celkem = detail
        ? KANALY.reduce((s, k) => s + (detail.kanaly[k.id]?.naklad || 0), 0)
        : 0;

    return (
        <div className="space-y-3">
            {/* Výběr předpisu */}
            <div className="rounded-xl p-4" style={{ border: `1px solid ${borderColor}`, background: isDark ? 'rgba(255,255,255,0.02)' : 'rgba(0,0,0,0.01)' }}>
                <div className="flex items-center gap-3 flex-wrap">
                    <ClipboardList size={15} style={{ color: BALIRNA, flexShrink: 0 }} />
                    <span className="text-xs font-medium" style={{ color: secondaryColor }}>Balicí předpis</span>
                    <div style={{ minWidth: 260, flex: 1, maxWidth: 420 }}>
                        <Dropdown
                            isDark={isDark} height={34} accent={BALIRNA}
                            placeholder="Bez předpisu — nic se nebalí"
                            value={recept?.balici_predpis_id || ''}
                            options={predpisy.map(p => ({ value: p.id, label: `${p.nazev} (${p.kod})` }))}
                            onChange={priradit}
                        />
                    </div>
                    {recept?.balici_predpis_id && (
                        <button onClick={() => priradit(null)} title="Odebrat předpis"
                            className="flex items-center gap-1 text-[11px]"
                            style={{ background: 'none', border: 'none', cursor: 'pointer', color: mutedColor }}>
                            <X size={12} /> odebrat
                        </button>
                    )}
                    {uklada && <Loader2 size={13} className="animate-spin" style={{ color: mutedColor }} />}
                </div>
                {chyba && <p className="text-[11px] mt-2" style={{ color: '#EF4444' }}>{chyba}</p>}
            </div>

            {!recept?.balici_predpis_id ? (
                <div className="rounded-xl p-8 text-center" style={{ border: `1px dashed ${borderColor}` }}>
                    <AlertTriangle size={24} style={{ color: '#F97316', margin: '0 auto 10px' }} />
                    <p className="text-sm" style={{ color: secondaryColor }}>Položka nemá balicí předpis</p>
                    <p className="text-xs mt-1" style={{ color: mutedColor }}>
                        Do nákladu se proto nezapočítá žádné balení. Vyber předpis výše,
                        nebo ho založ v Balírně.
                    </p>
                </div>
            ) : nacitam ? (
                <div className="flex justify-center py-8"><Loader2 size={18} className="animate-spin" style={{ color: mutedColor }} /></div>
            ) : detail && (
                <>
                    <div className="flex items-center gap-3 px-1">
                        <span className="text-[11px]" style={{ color: mutedColor }}>
                            Předpis sdílí {detail.recepty_count} položek — upravuje se v Balírně, tady je jen ke čtení.
                        </span>
                        {onNavigate && (
                            <button onClick={() => onNavigate('balirna', { tab: 'predpisy' })}
                                className="flex items-center gap-1 text-[11px]"
                                style={{ background: 'none', border: 'none', cursor: 'pointer', color: BALIRNA }}>
                                Otevřít v Balírně <ExternalLink size={11} />
                            </button>
                        )}
                    </div>

                    <div className="grid gap-3" style={{ gridTemplateColumns: 'repeat(3, minmax(0, 1fr))' }}>
                        {KANALY.map(kanal => {
                            const k = detail.kanaly[kanal.id];
                            return (
                                <div key={kanal.id} className="rounded-xl" style={{ border: `1px solid ${borderColor}` }}>
                                    <div className="flex items-center justify-between px-3 py-2" style={{ borderBottom: `1px solid ${borderColor}` }}>
                                        <div className="min-w-0">
                                            <div className="text-xs font-semibold" style={{ color: 'var(--text-primary)' }}>{kanal.label}</div>
                                            <div className="text-[10px]" style={{ color: mutedColor }}>{kanal.popis}</div>
                                        </div>
                                        <span className="text-sm font-bold" style={{ color: k.naklad > 0 ? BALIRNA : mutedColor }}>
                                            {fmtCena(k.naklad)}
                                        </span>
                                    </div>

                                    <div className="p-3">
                                        {k.polozky.length === 0 ? (
                                            <p className="text-[11px]" style={{ color: mutedColor }}>Nebalí se</p>
                                        ) : k.polozky.map(r => (
                                            <div key={r.id} className="flex items-center gap-1.5 py-1">
                                                <TypBadge typ={r.typ} />
                                                <span className="text-[11px] flex-1 truncate" style={{ color: 'var(--text-primary)' }}>{r.nazev}</span>
                                                <span className="text-[10px]" style={{ color: mutedColor }}>{r.spotreba} {r.jednotka}</span>
                                                <span className="text-[11px] font-medium" style={{ color: r.cena_chybi ? '#EF4444' : secondaryColor, minWidth: 52, textAlign: 'right' }}>
                                                    {r.cena_chybi ? 'bez ceny' : fmtCena(r.naklad)}
                                                </span>
                                            </div>
                                        ))}

                                        {k.media.length > 0 && (
                                            <div className="flex gap-1.5 mt-2 flex-wrap">
                                                {k.media.map(m => (
                                                    <a key={m.id} href={m.soubor?.url} target="_blank" rel="noreferrer"
                                                        className="rounded-lg overflow-hidden" style={{ width: 56, height: 56, border: `1px solid ${borderColor}`, display: 'block' }}>
                                                        <img src={m.soubor?.thumbnail_url || m.soubor?.url} alt=""
                                                            style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                                                    </a>
                                                ))}
                                            </div>
                                        )}
                                    </div>
                                </div>
                            );
                        })}
                    </div>

                    {celkem === 0 && (
                        <p className="text-[11px] px-1" style={{ color: mutedColor }}>
                            Předpis zatím nemá žádné položky — doplň mu je v Balírně.
                        </p>
                    )}
                </>
            )}
        </div>
    );
}
